// src/services/apiService.ts

import {
  UserProfile,
  EmailRuns,
  RunId,
  ResourceStatus,
  ProcessingSummary,
  ReferenceData,
  SupportedRunMode,
  SupportedDocumentType,
} from "@/lib/types";
import { apiClient } from "./client";
import { sessionService } from "./session";
import { coded, failure } from "@/lib/errors";

/**
 * Parse JWT token to extract expiry timestamp
 */
function parseJwtExpiry(token: string): number | undefined {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp ? payload.exp * 1000 : undefined; // Convert to milliseconds
  } catch {
    return undefined;
  }
}

export const apiService = {
  /**
   * Exchanges a Microsoft token for the application's own JWTs by calling the backend.
   * @param msToken The access token obtained from OfficeRuntime.auth.getAccessToken.
   * @returns The user's profile upon successful login.
   */
  async loginWithMicrosoft(msToken: string): Promise<UserProfile> {
    const response = await fetch("/api/token/microsoft", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ms_token: msToken }),
    }).catch(() => Promise.reject(coded({ id: "error-network" })));

    if (!response.ok) {
      throw await failure(response, "connection-error-token-exchange");
    }

    const tokens = await response.json();

    // Parse JWT tokens to extract expiry times
    const accessExpiry = parseJwtExpiry(tokens.access);
    const refreshExpiry = parseJwtExpiry(tokens.refresh);

    sessionService.setTokens({
      access: tokens.access,
      refresh: tokens.refresh,
      accessExpiry,
      refreshExpiry,
    });

    // After setting the tokens, fetch the user's profile to complete the login process.
    return this.fetchUserProfile();
  },

  logout(): void {
    sessionService.clearTokens();
    // Optionally, you could also call a backend endpoint to invalidate the refresh token.
  },

  isAuthenticated(): boolean {
    return !!sessionService.getTokens();
  },

  async fetchUserProfile(): Promise<UserProfile> {
    const response = await apiClient.fetch("/me");
    if (!response.ok) throw await failure(response, "error-read-profile");
    return response.json();
  },

  async initUploads(files: { filename: string; size: number }[]): Promise<
    {
      upload_id: string;
      blob_path: string;
      put_url: string;
      // List of {name, value} pairs rather than a mapping — shared
      // wire format with the Website client, where the Website client response
      // transformer would otherwise camelCase the header names.
      headers: { name: string; value: string }[];
      expires_at: string;
      max_bytes: number;
    }[]
  > {
    const response = await apiClient.fetch("/uploads/init", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ files }),
    });
    if (!response.ok) {
      throw await failure(response, "error-init-upload");
    }
    const result = await response.json();
    return result.uploads;
  },

  async uploadEmailData(body: {
    email: {
      id: string;
      subject: string;
      addresses: string[];
      body: string;
      receivedAt?: string | null;
    };
    attachments: {
      uploadId: string;
      resourceId: string;
      fileName: string;
      documentType: string;
      runMode?: string;
      routingMode?: string;
    }[];
    emailBody?: {
      resourceId: string;
      body: string;
      documentType: string;
      runMode?: string;
      routingMode?: string;
    } | null;
  }): Promise<{ attachmentId: string; id: RunId }[]> {
    const response = await apiClient.fetch("/upload", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw await failure(response, "error-upload-email");
    }
    const result = await response.json();
    return result.runs;
  },

  async getReference(): Promise<ReferenceData> {
    const response = await apiClient.fetch("/reference");
    if (!response.ok) throw new Error("Failed to fetch reference.");
    return response.json();
  },

  async getProcessings(): Promise<ProcessingSummary[]> {
    const response = await apiClient.fetch("/processings");
    if (!response.ok) throw new Error("Failed to fetch processings.");
    const data = await response.json();
    return data.processings ?? [];
  },

  async setProcessingPreference(documentType: string, defaultRunMode: SupportedRunMode): Promise<void> {
    const response = await apiClient.fetch("/processings/preference", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kind: "leon", documentType, defaultRunMode }),
    });
    if (!response.ok) {
      throw await failure(response, "error-save-preference");
    }
  },

  async deleteProcessingPreference(documentType: string): Promise<void> {
    const response = await apiClient.fetch("/processings/preference", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ kind: "leon", documentType }),
    });
    if (!response.ok && response.status !== 204) {
      throw await failure(response, "error-delete-preference");
    }
  },

  /**
   * The document type this email was last treated as (from its most recent
   * pipeline run), so re-opening it restores the same type view. `null` when
   * the email has never been processed by this user.
   */
  async fetchEmailDocumentType(emailId: string): Promise<SupportedDocumentType | null> {
    const response = await apiClient.fetch(`/email-document-type/${encodeURIComponent(emailId)}`);
    if (!response.ok) throw new Error(`Failed to fetch document type for ${emailId}`);
    const data = await response.json();
    return data.documentType ?? null;
  },

  async fetchEmailRuns(emailId: string): Promise<EmailRuns> {
    const response = await apiClient.fetch(`/emails/${encodeURIComponent(emailId)}/runs`);
    if (!response.ok) throw await failure(response, "status-read-failed");
    return response.json();
  },

  async retryRun(run: ResourceStatus): Promise<ResourceStatus> {
    const response = await apiClient.fetch(`/runs/${run.id}/retry`, {
      method: "POST",
      headers: { "Idempotency-Key": `${run.id}:${run.version ?? 0}:retry` },
    });
    if (!response.ok) throw new Error(`Failed to retry run ${run.id}`);
    return response.json();
  },
};
